import { normalizeSectionId } from './section-ids';
import type {
  Rectangle,
  SectionBestPrice,
  SectionMeasurement,
  ViewBox,
} from './types';

function toRectangle(rectangle: DOMRect): Rectangle | null {
  const values = [
    rectangle.left,
    rectangle.top,
    rectangle.right,
    rectangle.bottom,
    rectangle.width,
    rectangle.height,
  ];

  if (
    !values.every(Number.isFinite) ||
    rectangle.width <= 0 ||
    rectangle.height <= 0
  ) {
    return null;
  }

  return {
    left: rectangle.left,
    top: rectangle.top,
    right: rectangle.right,
    bottom: rectangle.bottom,
    width: rectangle.width,
    height: rectangle.height,
  };
}

function getBestAnchorRectangle(
  rectangles: readonly Rectangle[] | undefined,
  viewport: Rectangle
): Rectangle | null {
  if (!rectangles?.length) return null;

  return [...rectangles].sort((left, right) => {
    const visibleAreaDifference =
      getIntersectionArea(right, viewport) -
      getIntersectionArea(left, viewport);

    return (
      visibleAreaDifference ||
      right.width * right.height - left.width * left.height
    );
  })[0];
}

function getIntersectionArea(first: Rectangle, second: Rectangle): number {
  const width = Math.max(
    0,
    Math.min(first.right, second.right) - Math.max(first.left, second.left)
  );
  const height = Math.max(
    0,
    Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top)
  );

  return width * height;
}

export function getViewBox(svg: SVGSVGElement): ViewBox | null {
  const viewBox = svg.viewBox?.baseVal;
  if (
    !viewBox ||
    !Number.isFinite(viewBox.width) ||
    !Number.isFinite(viewBox.height)
  ) {
    return null;
  }

  if (viewBox.width <= 0 || viewBox.height <= 0) return null;

  return { width: viewBox.width, height: viewBox.height };
}

export function getSectionMeasurements({
  mapRoot,
  container,
  sections,
  rectangleCache = new Map<Element, Rectangle>(),
}: {
  mapRoot: HTMLElement;
  container: HTMLElement;
  sections: readonly SectionBestPrice[];
  rectangleCache?: Map<Element, Rectangle>;
}): SectionMeasurement[] {
  const containerRect = toRectangle(container.getBoundingClientRect());
  if (!containerRect) return [];

  const knownSections = new Set(sections.map(({ sectionId }) => sectionId));
  const sectionRectangles = new Map<string, Rectangle[]>();

  mapRoot.querySelectorAll('[data-section-id]').forEach((element) => {
    const sectionId = normalizeSectionId(
      element.getAttribute('data-section-id') ?? ''
    );

    if (!knownSections.has(sectionId)) return;

    const rectangle =
      rectangleCache.get(element) ??
      toRectangle(element.getBoundingClientRect());
    if (!rectangle) return;
    rectangleCache.set(element, rectangle);

    const rectangles = sectionRectangles.get(sectionId) ?? [];
    rectangles.push(rectangle);
    sectionRectangles.set(sectionId, rectangles);
  });

  return sections.flatMap((section) => {
    const rectangle = getBestAnchorRectangle(
      sectionRectangles.get(section.sectionId),
      containerRect
    );
    if (!rectangle) return [];

    const relativeLeft = rectangle.left - containerRect.left;
    const relativeTop = rectangle.top - containerRect.top;
    const relativeRight = rectangle.right - containerRect.left;
    const relativeBottom = rectangle.bottom - containerRect.top;
    const centerX = relativeLeft + rectangle.width / 2;
    const centerY = relativeTop + rectangle.height / 2;

    return [
      {
        ...section,
        centerX,
        centerY,
        width: rectangle.width,
        height: rectangle.height,
        visible:
          relativeRight > 0 &&
          relativeBottom > 0 &&
          relativeLeft < containerRect.width &&
          relativeTop < containerRect.height &&
          centerX >= 0 &&
          centerX <= containerRect.width &&
          centerY >= 0 &&
          centerY <= containerRect.height,
      },
    ];
  });
}
